import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@mui/material";
import TableFilterHeader from "../../components/TableHeader/TableFilterHeader";
import BaseCustomerTable from "../../components/BaseCustomerTable/BaseCustomerTable";
import PayrollAdminModal from "../../components/PayrollAdminModal/PayrollAdminModal";
import UserFace from "../../images/user.png";
import EditIcon from "../../images/editpencil.svg"; // ← add
import { commonRunQuery } from "../../util/API/loans";
import { getUserDetails } from "../../util/API/customers"; // ← add
import { decodeHtmlEntities } from "../../util/utilfunctions/utilFunctions";

/* ─── status badge colours ─────────────────────────────────────── */
const statusLabel = (status, userStatus = []) => {
  const found = userStatus.find(
    (s) => s?.userstatus?.statusID === String(status),
  );
  return found?.userstatus?.name ?? (status === "1" ? "Active" : "Inactive");
};

export default function ViewAssociates({
  parentAdmin,
  systemRoles,
  userStatus,
  onGoBack,
  updateUser, // ← needed by PayrollAdminModal
  setUpdateUser, // ← needed by PayrollAdminModal
  payrollUsers, // ← needed by PayrollAdminModal
}) {
  const [associates, setAssociates] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5);
  const [lastVisitedRow, setLastVisited] = useState(null);

  /* ── Modify Roles modal state (mirrors PayrollAdminTable) ── */
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const parentId = parentAdmin?.User?.id;
  const parentCompany = parentAdmin?.User?.otherNames || "—";
  const parentName =
    `${parentAdmin?.User?.firstName ?? ""} ${parentAdmin?.User?.lastName ?? ""}`.trim();
  const parentEmail = parentAdmin?.User?.other1 ?? "";

  const cleanName = (name) =>
    decodeHtmlEntities(decodeHtmlEntities(name || ""))
      .replace(/[^a-zA-Z0-9\s'&.-]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  // /* ── fetch associates ─────────────────────────────────────────── */
  // const fetchAssociates = async () => {
  //   setIsLoading(true);
  //   try {
  //     const res = await commonRunQuery(381, parentId);
  //     const raw = res?.data?.body?.record;
  //     const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  //     setAssociates(list);
  //     setFiltered(list);
  //   } catch (err) {
  //     console.error("fetchAssociates error:", err);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  const fetchAssociates = async () => {
  setIsLoading(true);
  try {
    const res = await commonRunQuery(381, parentId);
    const raw = res?.data?.body?.record;

    // Cake5 returns record:"" on empty; Cake2 also does — guard both
    let list = [];
    if (Array.isArray(raw)) {
      list = raw;
    } else if (raw && typeof raw === "object") {
      list = [raw];
    }
    // raw === "" or raw === null/undefined → list stays []

    setAssociates(list);
    setFiltered(list);
  } catch (err) {
    console.error("fetchAssociates error:", err);
  } finally {
    setIsLoading(false);
  }
};

  useEffect(() => {
    if (parentId) fetchAssociates();
  }, [parentId]);

  /* ── search ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (!searchText.trim()) {
      setFiltered(associates);
      return;
    }
    const q = searchText.toLowerCase();
    setFiltered(
      associates.filter(
        (r) =>
          r?.u?.firstName?.toLowerCase().includes(q) ||
          r?.u?.lastName?.toLowerCase().includes(q) ||
          (r?.u?.emailAccount || "").toLowerCase().includes(q) ||
          r?.u?.companyName?.toLowerCase().includes(q),
      ),
    );
    setPage(0);
  }, [searchText, associates]);

  /* ── Modify Roles handler (same pattern as PayrollAdminTable) ─── */
  const handleModifyRoles = async (associateUserId) => {
    const response = await getUserDetails(associateUserId);
    setSelectedUser(response);
    setModalOpen(true);
  };

  /* ── pagination ───────────────────────────────────────────────── */
  const pageCount = Math.ceil(filtered.length / rowsPerPage);
  const pageNumbers = Array.from({ length: pageCount }, (_, i) => i);
  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  /* ── render ───────────────────────────────────────────────────── */
  return (
    <Box>
      {/* ── Modify Roles Modal (same props as PayrollAdminTable) ── */}
      <PayrollAdminModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        selectedUser={selectedUser}
        payrollFilterList={payrollUsers} // associates share the same user pool
        updateUser={updateUser}
        setUpdateUser={setUpdateUser}
        userStatus={userStatus}
        userRoles={systemRoles}
        payrollUsers={payrollUsers}
        handleSelectedUser={handleModifyRoles}
      />

      {/* ── Go Back ── */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          onClick={onGoBack}
          sx={{
            color: "#00365A",
            fontFamily: "Inter",
            fontSize: "14px",
            fontWeight: 500,
            borderRadius: "30px",
            border: "1px solid #EAEEF4",
            background: "#FFF",
            padding: "5px 20px",
          }}
        >
          Go Back
        </Button>
      </Box>

      {/* ── Parent admin banner ── */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: "#15803d" }}
          >
            Associates under: {parentName}
          </Typography>
          <Typography variant="body2" sx={{ color: "#166534" }}>
            {parentEmail} &nbsp;·&nbsp; {cleanName(parentCompany)}
          </Typography>
        </Box>
        <Chip
          label={`${associates.length} associate${associates.length !== 1 ? "s" : ""}`}
          size="small"
          sx={{
            ml: "auto",
            backgroundColor: "#dcfce7",
            color: "#166534",
            fontWeight: 600,
          }}
        />
      </Box>

      {/* ── Search bar ── */}
      <TableFilterHeader
        handleSearch={(val) => setSearchText(val)}
        setOpen={() => {}}
        createAllowed={false}
        placeholder="Search Associates"
      />

      {/* ── Table ── */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography color="text.secondary">
            No associates found under this payroll admin.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: "none", mt: 1 }}>
          <Table sx={{ minWidth: 500 }} aria-label="associates table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>
                  <Typography className="customer-table-headers">
                    Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Company
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Email
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Phone
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Role
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Account Status
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Date Added
                  </Typography>
                </TableCell>
                <TableCell align="left">
                  <Typography className="customer-table-headers">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginated.map((row) => (
                <TableRow
                  key={row?.u?.id}
                  onClick={() => setLastVisited(row?.u?.id)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      row?.u?.id === lastVisitedRow ? "#dbefff" : "inherit",
                    "&:hover": { backgroundColor: "#f0f7ff" },
                  }}
                >
                  {/* Avatar */}
                  <TableCell style={{ width: 50 }}>
                    <img
                      src={UserFace}
                      className="table-customer-photo"
                      alt="user"
                    />
                  </TableCell>

                  {/* Name */}
                  <TableCell style={{ width: 180 }}>
                    <Typography className="email-admin-text">
                      {row?.u?.firstName} {row?.u?.lastName}
                    </Typography>
                  </TableCell>

                  {/* Company */}
                  <TableCell style={{ width: 180 }}>
                    <Typography
                      className="email-admin-text"
                      sx={{ fontWeight: 500 }}
                    >
                      {cleanName(row?.u?.companyName) || "—"}
                    </Typography>
                  </TableCell>

                  {/* Email */}
                  <TableCell style={{ width: 200 }}>
                    <Typography className="email-admin-text">
                      {row?.u?.emailAccount || "—"}
                    </Typography>
                  </TableCell>

                  {/* Phone */}
                  <TableCell style={{ width: 150 }}>
                    <Typography className="email-admin-text">
                      {row?.u?.phoneno || "—"}
                    </Typography>
                  </TableCell>

                  {/* Role badge */}
                  <TableCell style={{ width: 160 }}>
                    <Chip
                      label={row?.sr?.roleName || "Associate Payroll Admin"}
                      size="small"
                      sx={{
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        fontWeight: 500,
                        fontSize: "11px",
                      }}
                    />
                  </TableCell>

                  {/* Status */}
                  <TableCell style={{ width: 130 }}>
                    <span
                      className={
                        String(row?.u?.status) === "1"
                          ? "active-user"
                          : "disabled-user"
                      }
                    >
                      {statusLabel(row?.u?.status, userStatus)}
                    </span>
                  </TableCell>

                  {/* Date added */}
                  <TableCell style={{ width: 160 }}>
                    <Typography className="email-admin-text">
                      {row?.u?.dateAdded
                        ? new Date(row.u.dateAdded).toLocaleDateString()
                        : "—"}
                    </Typography>
                  </TableCell>

                  {/* Actions — stop propagation so row highlight doesn't fire */}
                  <TableCell
                    style={{ width: 160 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box
                      className="modify-role-button"
                      onClick={() => handleModifyRoles(row?.u?.id)}
                    >
                      <img src={EditIcon} alt="Edit" />
                      Modify Roles
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <BaseCustomerTable
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            pageNumbers={pageNumbers}
          />
        </TableContainer>
      )}
    </Box>
  );
  
}
